import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { combineLatest, Observable, of, filter, map, switchMap, take } from 'rxjs';

import { PageSlotModule } from '@spartacus/storefront';

import { AbstractPageComponent } from '@seb/web-common/core/templates/abstract-page/abstract-page.component';
import { CssHierarchy, CustomCategory, UrlMapping } from '@seb/web-common/core/models';
import { BaseStoreService, ImageService } from '@seb/web-common/core/services';
import { CoreModalService, ILink } from '@seb/web-design-system/core';
import { ISubcategory } from '@seb/web-design-system/subcategories';
import { LinkMapper } from '@seb/web-common/core/mappers';

import { AccessoryCategoriesComponent } from '../../components/accessory-categories/accessory-categories.component';
import { AccessorySearchComponent } from '../../components/accessory-search/accessory-search.component';

@Component({
  selector: 'wc-accessory-page',
  templateUrl: './accessory-page.component.html',
  styleUrls: ['./accessory-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    PageSlotModule,
    AccessorySearchComponent,
    AccessoryCategoriesComponent
  ],
  providers: [CoreModalService]
})
export class AccessoryPageComponent extends AbstractPageComponent implements OnInit {
  categories: CustomCategory[];
  subcategories: ISubcategory[];
  eagerLoaded = false;
  searchLoaded = false;
  isLazyLoadingEnabled = true;

  constructor(
    private imageService: ImageService,
    private baseStoreService: BaseStoreService,
    private cdRef: ChangeDetectorRef
  ) {
    super();

    this.eagerLoadingSlot = 'Arguments';
    this.eagerLoadingLimit = 1;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.isLazyLoadingEnabled = this.lazyLoadingService.isEnabled;

    this.subscriptions.add(
      this.lazyLoadingService.canLazyLoad.subscribe((canLazyLoad) => {
        this.eagerLoaded = canLazyLoad;
      })
    );

    this.subscriptions.add(
      this.baseStoreService.getSisCategories('ACCESSORY', '', 2).pipe(
        take(1),
        switchMap((sisCategories) => combineLatest([
          of(sisCategories),
          ...this.toSubCategories(sisCategories.accessoryCategory?.subCategories)
        ]))
      ).subscribe(([sisCategories, ...subCategories]: [CssHierarchy, ISubcategory]) => {
        this.categories = sisCategories.accessoryCategory?.subCategories || [];
        this.subcategories = subCategories;
        this.searchLoaded = true;
        this.cdRef.markForCheck();
      })
    );
  }

  private toSubCategories(categories: CustomCategory[]): Observable<ISubcategory>[] {
    return categories?.map((subCate) => this.subCategoryToISubcategory(subCate));
  }

  private subCategoryToISubcategory(category: CustomCategory): Observable<ISubcategory> {
    const categoryUrl = LinkMapper.decodeUrl(category.url);
    const subSubCategories$: Observable<ILink>[] = category.subCategories.map((sub) => this.subSubCategoryToILink(sub));

    return combineLatest([this.urlMappingService.getUrlMappingByCode(categoryUrl), ...subSubCategories$]).pipe(
      map(([urlMapping, ...subSubCategories]: [UrlMapping, ILink]): ISubcategory => {
        let finalCategoryUrl = categoryUrl;

        if (urlMapping) {
          finalCategoryUrl = categoryUrl.replace(urlMapping.code, urlMapping.value);
        }

        return {
          name: category.name,
          link: {
            url: LinkMapper.decodeUrl(finalCategoryUrl),
            linkName: category.name
          },
          image: this.imageService.normalizeMedia(category.image),
          list: {
            icon: 'icon-dot',
            items: subSubCategories
          }
        };
      })
    );
  }

  private subSubCategoryToILink(subCategory: CustomCategory): Observable<ILink> {
    const subCategoryUrl = LinkMapper.decodeUrl(subCategory.url);

    return this.urlMappingService.getUrlMappingByCode(subCategoryUrl).pipe(
      filter((u) => !!u),
      map((urlMapping: UrlMapping) => {
        let finalSubCategoryUrl = subCategoryUrl;

        if (urlMapping) {
          finalSubCategoryUrl = subCategoryUrl.replace(urlMapping.code, urlMapping.value);
        }

        return {
          url: finalSubCategoryUrl,
          linkName: subCategory.name
        };
      })
    );
  }
}
